/**
 * Motor de Simulação em Lote da Reforma Tributária (CBS + IBS + IS)
 * R-MAX Precificação Inteligente
 * 
 * Regras:
 * - IVA Dual: CBS (8,8% Fazenda / 11,3% TCU) + IBS (17,7% Fazenda / 16,7% TCU)
 * - Mercadorias (NCM) e Serviços (NBS)
 * - Matriz Oficial de Reduções (LC 214/2025):
 *   - Alíquota Zero (Redução 100%): Cesta básica, medicamentos específicos, Prouni
 *   - Redução de 60%: Serviços de saúde humana, hospitais, educação, medicamentos gerais
 *   - Redução de 30%: Profissões intelectuais regulamentadas (Art. 136 - advogados, contadores, engenheiros, médicos autônomos)
 *   - Alíquota Padrão: Sem redução (28,0% TCU ou 26,5% Fazenda)
 *   - Imposto Seletivo: Sobretaxa em bebidas e cigarros
 * - Simulação com Notas de Entrada (Compras) e Notas de Saída (Vendas / Split Payment)
 * - Arredondamento comercial: Degrau de 5 sempre para cima (Math.ceil(centavosAjustados / 5) * 5)
 */

export type SupplierTaxRegime = 'regime_geral' | 'simples_nacional' | 'isento_zero' | 'custom';
export type TaxReductionCategory = 'zero' | 'reducao_60' | 'reducao_30' | 'padrao' | 'seletivo';
export type FiscalType = 'NCM' | 'NBS';
export type BusinessSector = 'comercio' | 'servico' | 'industria';

export interface BatchItemInput {
  id?: string;
  sku?: string;
  name: string;
  ncm?: string;
  nbs?: string;
  fiscalCode?: string;
  itemType?: 'product' | 'service';
  taxReduction?: TaxReductionCategory;
  sector?: BusinessSector;
  cfop?: string;
  cfopDescription?: string;
  paymentTermDays?: number;  // Prazo médio concedido ao cliente em dias (PMR do item)
  purchaseTermDays?: number; // Prazo médio de pagamento a fornecedor em dias (PMP do item)
  
  // Parâmetros da Nota Fiscal de Entrada (Compra)
  costPrice: number;
  unitCost?: number;
  supplierTaxRegime?: SupplierTaxRegime;
  customInputCreditPct?: number;

  // Tributos Legados Destacados na NF de Entrada (para comparativo Ouro da Indústria)
  legacyIcmsVal?: number;
  legacyPisVal?: number;
  legacyCofinsVal?: number;
  legacyIssVal?: number;

  // Parâmetros da Nota Fiscal de Saída (Venda)
  currentSalePrice: number;
  simulatedSalePrice?: number;
  targetMarginPct?: number;
  activityType?: 'product' | 'service';
  serviceCategory?: 'general' | 'health_edu' | 'liberal_pro';
}

export type TaxRegimeMode = 'simples_hibrido' | 'simples_normal' | 'lucro_real';

export interface BatchItemSimulationResult {
  originalIndex: number;
  sku: string;
  name: string;
  fiscalType: FiscalType;
  fiscalCode: string;
  reductionCategory: TaxReductionCategory;
  reductionPct: number;
  reductionLabel: string;
  ncm: string; // Código NCM segregado (produtos físicos)
  nbs: string; // Código NBS segregado (serviços e intangíveis)
  destination: 'resale' | 'consumption' | 'raw_material' | 'service'; // Destinação da operação fiscal
  sector: BusinessSector;
  cfop?: string;
  paymentTermDays?: number;
  isSalesOnly: boolean;         // Flag: planilha só de vendas (custo zero ou não informado)
  recomposedSalePrice: number;  // Preço de recomposição protetiva para absorver o imposto

  // Nota de Entrada (Compra)
  originalCost: number;
  supplierTaxRegime: SupplierTaxRegime;
  inputCreditPct: number;
  inputCreditCbsRatePct: number; // Alíquota CBS de entrada
  inputCreditIbsRatePct: number; // Alíquota IBS de entrada
  inputCreditVal: number;
  inputCreditCbsVal: number;     // Valor R$ de crédito CBS
  inputCreditIbsVal: number;     // Valor R$ de crédito IBS
  netStockCost: number; // Custo líquido após apropriação do crédito

  // Nota de Saída (Venda)
  cbsRatePct: number;            // Alíquota CBS de saída
  ibsRatePct: number;            // Alíquota IBS de saída
  totalTaxRatePct: number;       // CBS + IBS total de saída
  currentSalePrice: number;
  suggestedSalePrice: number;    // Preço sugerido comercial (degrau de 5 para cima)
  simulatedSalePrice: number;    // Preço ativo praticado na simulação da NF de saída
  
  // Confronto Tributário (Split Payment)
  taxOnSaleVal: number;          // CBS + IBS total destacado na nota de saída
  taxOnSaleCbsVal: number;       // Valor R$ de CBS destacado na nota de saída
  taxOnSaleIbsVal: number;       // Valor R$ de IBS destacado na nota de saída
  netTaxPayableVal: number;      // CBS/IBS total a recolher = (Imposto Saída) - (Crédito Entrada)
  cbsToPayVal: number;           // CBS líquida a recolher
  ibsToPayVal: number;           // IBS líquido a recolher
  
  // Rentabilidade
  projectedMarginVal: number;    // Lucro bruto unitário pós-tributos (ou sobra líquida)
  projectedMarginPct: number;    // Margem bruta projetada
  priceDifference: number;       // simulatedSalePrice - currentSalePrice
  
  // Comparativo Legado vs Reforma Tributária (Ouro da Indústria)
  unitCost?: number;
  cfopDescription?: string;
  legacyCreditVal: number;          // Crédito aproveitável hoje sob regras atuais
  legacyIcmsVal: number;            // ICMS de compra informado na NF
  legacyPisVal: number;             // PIS de compra informado na NF
  legacyCofinsVal: number;          // COFINS de compra informado na NF
  reformCreditVal: number;          // Crédito pleno CBS + IBS (28%)
  reformCreditGainVal: number;      // Ganho Líquido no Caixa = reformCreditVal - legacyCreditVal
  isCreditDisallowedToday: boolean; // Vedado hoje (ex: CFOP 1556 / Uso & Consumo ou ISS)

  status: 'HEALTHY' | 'COMPRESSED' | 'LOSS';
  statusLabel: string;
  classificationTag: string;     // Rótulo completo do enquadramento
}

export interface WorkingCapitalMetrics {
  avgPmpDays: number;            // Prazo Médio de Pagamento a Fornecedores (dias)
  avgPmrDays: number;            // Prazo Médio de Recebimento de Vendas (dias)
  avgPmeDays: number;            // Prazo Médio de Giro de Estoque (dias)
  operatingCycleDays: number;    // Ciclo Operacional = PME + PMR
  financialCycleDays: number;    // Ciclo Financeiro (Descasamento) = (PME + PMR) - PMP
  splitPaymentDrainVal: number;  // Imposto retido imediatamente na fonte pelo Split Payment (impacto na liquidez)
  idealReceivingDaysForSplit: number; // Prazo máximo ideal de venda para não asfixiar o capital de giro
}

export interface BatchSimulationSummary {
  totalItems: number;
  healthyCount: number;
  compressedCount: number;
  lossCount: number;
  totalPurchasesVal: number;        // Volume financeiro total das compras/insumos
  totalStockCostBefore: number;
  totalStockCostAfter: number;
  totalInputCreditsGained: number;
  totalCbsCreditGained: number;  // Crédito total de CBS nas compras
  totalIbsCreditGained: number;  // Crédito total de IBS nas compras
  totalConsumptionItems: number; // Quantidade de itens de uso e consumo
  totalConsumptionCreditsGained: number; // Crédito gerado por Uso/Consumo (ganho novo da Reforma)
  totalTaxOnSale: number;        // Débitos da Nova Reforma (CBS + IBS na saída)
  totalCbsDebitOnSale: number;   // Débito total de CBS nas vendas
  totalIbsDebitOnSale: number;   // Débito total de IBS nas vendas
  totalNetTaxPayable: number;    // Débito Líquido a Recolher no Split (Saída - Entrada)
  totalLegacyCreditsVal: number; // Créditos aproveitáveis hoje no regime legado
  totalReformCreditsVal: number; // Créditos totais sob a Reforma Tributária
  totalNetCreditGainVal: number; // Ganho Líquido Total em Caixa (+R$)
  disallowedCreditsCount: number;// Quantidade de insumos com crédito vedado hoje
  averageCurrentMarginPct: number;
  averageProjectedMarginPct: number;
  workingCapitalMetrics?: WorkingCapitalMetrics;
}

/**
 * Arredondamento Comercial Obrigatório do R-MAX
 * Degrau de 5 sempre para cima, com higienização de ponto flutuante.
 */
export const arredondarPrecoFinal = (valor: number): number => {
  if (!Number.isFinite(valor) || valor <= 0) return 0;
  const centavosAjustados = Math.round(valor * 100) / 100;
  return Math.ceil(centavosAjustados / 5) * 5;
};

export type BatchBenchmarkTaxSource = 'tcu_2026' | 'fazenda_preliminar_8_8';

export interface FiscalClassificationResult {
  fiscalType: FiscalType;
  fiscalCode: string;
  category: TaxReductionCategory;
  reductionPct: number;
  cbsRate: number;
  ibsRate: number;
  totalRate: number;
  label: string;
}

/**
 * Classificação Fiscal Universal (NCM e NBS) com Matriz de Reduções da LC 214/2025
 */
export function classifyFiscalCode(
  codeRaw?: string,
  explicitType?: 'product' | 'service',
  explicitReduction?: TaxReductionCategory,
  benchmarkSource: BatchBenchmarkTaxSource = 'tcu_2026'
): FiscalClassificationResult {
  const isTcu = benchmarkSource === 'tcu_2026';
  const standardCbs = isTcu ? 11.3 : 8.8; // TCU 11,3% (TC 006.529/2026-7) vs Fazenda 8,8%
  const standardIbs = isTcu ? 16.7 : 17.7;
  const standardTotal = Number((standardCbs + standardIbs).toFixed(2));

  const raw = (codeRaw || '').trim();
  const digits = raw.replace(/\D/g, '');

  // Detecta se é NBS (Serviço) ou NCM (Produto)
  const isNbs = 
    explicitType === 'service' ? true :
    explicitType === 'product' ? false :
    raw.startsWith('1.') || 
    raw.toLowerCase().includes('nbs') || 
    (digits.length === 9 && digits.startsWith('1'));

  const fiscalType: FiscalType = isNbs ? 'NBS' : 'NCM';
  const fiscalCode = raw || (fiscalType === 'NBS' ? '1.0101.10.00' : '0000.00.00');

  // 1. Se o usuário definiu uma redução explícita, honra diretamente
  if (explicitReduction) {
    if (explicitReduction === 'zero') {
      return {
        fiscalType,
        fiscalCode,
        category: 'zero',
        reductionPct: 100,
        cbsRate: 0,
        ibsRate: 0,
        totalRate: 0,
        label: `${fiscalType} ${fiscalCode} • Alíquota Zero (Redução Integral)`
      };
    }
    if (explicitReduction === 'reducao_60') {
      const redCbs = Number((standardCbs * 0.40).toFixed(2));
      const redIbs = Number((standardIbs * 0.40).toFixed(2));
      const total = Number((redCbs + redIbs).toFixed(2));
      return {
        fiscalType,
        fiscalCode,
        category: 'reducao_60',
        reductionPct: 60,
        cbsRate: redCbs,
        ibsRate: redIbs,
        totalRate: total,
        label: `${fiscalType} ${fiscalCode} • Redução de 60% (${total.toFixed(1)}%)`
      };
    }
    if (explicitReduction === 'reducao_30') {
      const redCbs = Number((standardCbs * 0.70).toFixed(2));
      const redIbs = Number((standardIbs * 0.70).toFixed(2));
      const total = Number((redCbs + redIbs).toFixed(2));
      return {
        fiscalType,
        fiscalCode,
        category: 'reducao_30',
        reductionPct: 30,
        cbsRate: redCbs,
        ibsRate: redIbs,
        totalRate: total,
        label: `${fiscalType} ${fiscalCode} • Redução de 30% (${total.toFixed(1)}%)`
      };
    }
    if (explicitReduction === 'seletivo') {
      return {
        fiscalType,
        fiscalCode,
        category: 'seletivo',
        reductionPct: 0,
        cbsRate: standardCbs,
        ibsRate: standardIbs,
        totalRate: standardTotal,
        label: `${fiscalType} ${fiscalCode} • Imposto Seletivo (+${standardTotal.toFixed(1)}%)`
      };
    }
    return {
      fiscalType,
      fiscalCode,
      category: 'padrao',
      reductionPct: 0,
      cbsRate: standardCbs,
      ibsRate: standardIbs,
      totalRate: standardTotal,
      label: `${fiscalType} ${fiscalCode} • Alíquota Padrão (${standardTotal.toFixed(1)}%)`
    };
  }

  // 2. Classificação Automática para NBS (Serviços)
  if (isNbs) {
    // Serviços de Saúde Humana, Médicos, Clínicas e Educação (Redução de 60%)
    // Ex: NBS 1.25 (Saúde humana e assistência social), NBS 1.24 (Serviços de ensino/educação)
    if (raw.startsWith('1.25') || digits.startsWith('125') || raw.startsWith('1.24') || digits.startsWith('124')) {
      const redCbs = Number((standardCbs * 0.40).toFixed(2));
      const redIbs = Number((standardIbs * 0.40).toFixed(2));
      const total = Number((redCbs + redIbs).toFixed(2));
      return {
        fiscalType: 'NBS',
        fiscalCode,
        category: 'reducao_60',
        reductionPct: 60,
        cbsRate: redCbs,
        ibsRate: redIbs,
        totalRate: total,
        label: `NBS ${fiscalCode} • Saúde / Educação (Redução 60% — ${total.toFixed(1)}%)`
      };
    }

    // Serviços de Profissões Intelectuais Regulamentadas (Redução de 30% - Art. 136 da LC 214/2025)
    // Ex: Advogados, Contadores, Engenheiros, Arquitetos, Veterinários (NBS 1.1501, 1.1502, 1.1503, 1.1504)
    if (
      raw.startsWith('1.1501') || digits.startsWith('11501') ||
      raw.startsWith('1.1502') || digits.startsWith('11502') ||
      raw.startsWith('1.1503') || digits.startsWith('11503') ||
      raw.startsWith('1.1504') || digits.startsWith('11504') ||
      raw.startsWith('1.15') || digits.startsWith('115')
    ) {
      const redCbs = Number((standardCbs * 0.70).toFixed(2));
      const redIbs = Number((standardIbs * 0.70).toFixed(2));
      const total = Number((redCbs + redIbs).toFixed(2));
      return {
        fiscalType: 'NBS',
        fiscalCode,
        category: 'reducao_30',
        reductionPct: 30,
        cbsRate: redCbs,
        ibsRate: redIbs,
        totalRate: total,
        label: `NBS ${fiscalCode} • Profissões Regulamentadas (Redução 30% — ${total.toFixed(1)}%)`
      };
    }

    // Serviços Gerais (Alíquota Padrão)
    return {
      fiscalType: 'NBS',
      fiscalCode,
      category: 'padrao',
      reductionPct: 0,
      cbsRate: standardCbs,
      ibsRate: standardIbs,
      totalRate: standardTotal,
      label: `NBS ${fiscalCode} • Serviços Gerais (${standardTotal.toFixed(1)}%)`
    };
  }

  // 3. Classificação Automática para NCM (Mercadorias)
  // Cesta Básica Nacional (Alíquota Zero) - Ex: Arroz (1006), Feijão (0713), Carnes (0201, 0202, 0207), Leite (0401), Farinhas (1101, 1102)
  const cestaPrefixes = ['1006', '0713', '0201', '0202', '0207', '0401', '1905', '0402', '1101', '1102'];
  if (cestaPrefixes.some(p => digits.startsWith(p))) {
    return {
      fiscalType: 'NCM',
      fiscalCode,
      category: 'zero',
      reductionPct: 100,
      cbsRate: 0,
      ibsRate: 0,
      totalRate: 0,
      label: `NCM ${fiscalCode} • Cesta Básica (Alíquota Zero)`
    };
  }

  // Medicamentos / Saúde (Redução 60%) - Ex: NCM 3004, 3003, 3002, 9018, 9021
  if (
    digits.startsWith('3004') || digits.startsWith('3003') || digits.startsWith('3002') ||
    digits.startsWith('9018') || digits.startsWith('9021')
  ) {
    const redCbs = Number((standardCbs * 0.40).toFixed(2));
    const redIbs = Number((standardIbs * 0.40).toFixed(2));
    const total = Number((redCbs + redIbs).toFixed(2));
    return {
      fiscalType: 'NCM',
      fiscalCode,
      category: 'reducao_60',
      reductionPct: 60,
      cbsRate: redCbs,
      ibsRate: redIbs,
      totalRate: total,
      label: `NCM ${fiscalCode} • Medicamentos / Saúde (Redução 60% — ${total.toFixed(1)}%)`
    };
  }

  // Bebidas Alcoólicas e Tabaco (Imposto Seletivo) - Ex: Cerveja (2203), Destilados (2208), Cigarros (2402)
  if (digits.startsWith('2203') || digits.startsWith('2208') || digits.startsWith('2402')) {
    return {
      fiscalType: 'NCM',
      fiscalCode,
      category: 'seletivo',
      reductionPct: 0,
      cbsRate: standardCbs,
      ibsRate: standardIbs,
      totalRate: standardTotal,
      label: `NCM ${fiscalCode} • Imposto Seletivo (+${standardTotal.toFixed(1)}%)`
    };
  }

  // Alíquota Padrão (28,0% TCU ou 26,5% Fazenda)
  return {
    fiscalType: 'NCM',
    fiscalCode,
    category: 'padrao',
    reductionPct: 0,
    cbsRate: standardCbs,
    ibsRate: standardIbs,
    totalRate: standardTotal,
    label: `NCM ${fiscalCode} • Alíquota Padrão (${standardTotal.toFixed(1)}% — ${isTcu ? 'TCU 2026' : 'Fazenda'})`
  };
}

/**
 * Função de retrocompatibilidade para testes legados
 */
export function classifyNcm(
  ncmRaw?: string,
  benchmarkSource: BatchBenchmarkTaxSource = 'tcu_2026'
): {
  type: 'cesta_zero' | 'reducao_60' | 'seletivo' | 'padrao';
  cbsRate: number;
  ibsRate: number;
  label: string;
} {
  const res = classifyFiscalCode(ncmRaw, 'product', undefined, benchmarkSource);
  let type: 'cesta_zero' | 'reducao_60' | 'seletivo' | 'padrao' = 'padrao';
  if (res.category === 'zero') type = 'cesta_zero';
  else if (res.category === 'reducao_60') type = 'reducao_60';
  else if (res.category === 'seletivo') type = 'seletivo';

  return {
    type,
    cbsRate: res.cbsRate,
    ibsRate: res.ibsRate,
    label: res.label
  };
}

/**
 * Simula um item individual sob as regras da Reforma Tributária com simulação de NF Entrada vs Saída
 */
export function simulateItem(
  item: BatchItemInput,
  regime: TaxRegimeMode = 'simples_hibrido',
  defaultTargetMarginPct = 25,
  benchmarkSource: BatchBenchmarkTaxSource = 'tcu_2026'
): BatchItemSimulationResult {
  const costPrice = Math.max(0, item.costPrice || 0);
  const currentSalePrice = Math.max(0, item.currentSalePrice || 0);

  // 0. Segregação e Resolução de NCM e NBS
  const rawNcm = (item.ncm || '').trim();
  const rawNbs = (item.nbs || '').trim();
  let resolvedNcm = rawNcm;
  let resolvedNbs = rawNbs;

  // Classificação Fiscal do Item (NCM ou NBS)
  const fiscalCodeToUse = rawNbs || rawNcm || item.fiscalCode;
  const classification = classifyFiscalCode(
    fiscalCodeToUse,
    item.itemType || item.activityType,
    item.taxReduction,
    benchmarkSource
  );

  if (!resolvedNcm && !resolvedNbs) {
    if (classification.fiscalType === 'NBS') {
      resolvedNbs = classification.fiscalCode;
    } else {
      resolvedNcm = classification.fiscalCode;
    }
  } else if (!resolvedNcm && classification.fiscalType === 'NCM') {
    resolvedNcm = classification.fiscalCode;
  } else if (!resolvedNbs && classification.fiscalType === 'NBS') {
    resolvedNbs = classification.fiscalCode;
  }

  // Determinação Inteligente do Segmento e Destinação Fiscal (Revenda, Uso & Consumo, Matéria-Prima, Serviço)
  const cleanCfop = (item.cfop || '').replace(/\D/g, '');
  let destination: 'resale' | 'consumption' | 'raw_material' | 'service' = 'resale';
  let detectedSector: BusinessSector = 'comercio';

  if (
    cleanCfop.startsWith('1556') || cleanCfop.startsWith('2556') ||
    cleanCfop.startsWith('1407') || cleanCfop.startsWith('2407') ||
    cleanCfop.startsWith('1551') || cleanCfop.startsWith('2551') ||
    /sulfite|papel a4|material escritorio|limpeza|copo desc|desinfetante|consumo/i.test(item.name || '')
  ) {
    destination = 'consumption';
    detectedSector = 'comercio';
  } else if (
    cleanCfop.startsWith('1101') || cleanCfop.startsWith('2101') ||
    cleanCfop.startsWith('1111') || cleanCfop.startsWith('2111') ||
    cleanCfop.startsWith('5101') || cleanCfop.startsWith('6101') ||
    /manta|bobina|filtro|cabo|chapa|tubo|resina|embalagem|perfil/i.test(item.name || '')
  ) {
    destination = 'raw_material';
    detectedSector = 'industria';
  } else if (
    cleanCfop.startsWith('1933') || cleanCfop.startsWith('2933') ||
    cleanCfop.startsWith('5933') || cleanCfop.startsWith('6933') ||
    classification.fiscalType === 'NBS' || item.itemType === 'service'
  ) {
    destination = 'service';
    detectedSector = 'servico';
  } else {
    destination = 'resale';
    detectedSector = item.sector || 'comercio';
  }

  if (item.sector) {
    detectedSector = item.sector;
  }

  // 1. NOTA DE ENTRADA (COMPRA / FORNECEDOR)
  // Apuração do crédito fiscal apropriado
  const supplierTaxRegime: SupplierTaxRegime = item.supplierTaxRegime || 'regime_geral';
  let inputCreditPct = 0;
  let inputCreditVal = 0;
  let netStockCost = costPrice;

  if (regime === 'simples_hibrido' || regime === 'lucro_real') {
    if (supplierTaxRegime === 'regime_geral') {
      // Fornecedor Regime Geral destaca a alíquota cheia do insumo
      inputCreditPct = classification.totalRate;
    } else if (supplierTaxRegime === 'simples_nacional') {
      // Fornecedor Simples transfere crédito proporcional menor
      inputCreditPct = 4.0;
    } else if (supplierTaxRegime === 'custom') {
      inputCreditPct = Math.max(0, item.customInputCreditPct || 0);
    } else {
      // isento_zero
      inputCreditPct = 0;
    }

    if (inputCreditPct > 0 && costPrice > 0) {
      inputCreditVal = Math.round((costPrice * (inputCreditPct / (100 + inputCreditPct))) * 100) / 100;
      netStockCost = Math.max(0, costPrice - inputCreditVal);
    } else {
      netStockCost = costPrice;
      inputCreditVal = 0;
    }
  } else {
    // Simples Normal não credita na compra; custo de estoque é bruto
    netStockCost = costPrice;
    inputCreditVal = 0;
    inputCreditPct = 0;
  }

  // Decomposição dos impostos de Entrada (CBS e IBS)
  let inputCreditCbsRatePct = 0;
  let inputCreditIbsRatePct = 0;
  let inputCreditCbsVal = 0;
  let inputCreditIbsVal = 0;

  if (inputCreditVal > 0) {
    if (supplierTaxRegime === 'regime_geral') {
      inputCreditCbsRatePct = classification.cbsRate;
      inputCreditIbsRatePct = classification.ibsRate;
      const cbsRatio = classification.totalRate > 0 ? classification.cbsRate / classification.totalRate : 0.40;
      inputCreditCbsVal = Math.round(inputCreditVal * cbsRatio * 100) / 100;
      inputCreditIbsVal = Math.round((inputCreditVal - inputCreditCbsVal) * 100) / 100;
    } else {
      const isTcu = benchmarkSource === 'tcu_2026';
      const stdCbs = isTcu ? 11.3 : 8.8;
      const stdIbs = isTcu ? 16.7 : 17.7;
      const cbsRatio = stdCbs / (stdCbs + stdIbs);
      inputCreditCbsRatePct = Number((inputCreditPct * cbsRatio).toFixed(2));
      inputCreditIbsRatePct = Number((inputCreditPct - inputCreditCbsRatePct).toFixed(2));
      inputCreditCbsVal = Math.round(inputCreditVal * cbsRatio * 100) / 100;
      inputCreditIbsVal = Math.round((inputCreditVal - inputCreditCbsVal) * 100) / 100;
    }
  }

  // 1.1 COMPARATIVO TRIBUTÁRIO LEGADO (HOJE) VS REFORMA TRIBUTÁRIA (LC 214/2025)
  let legacyCreditVal = 0;
  let isCreditDisallowedToday = false;
  const legacyIcmsVal = Math.max(0, item.legacyIcmsVal || 0);
  const legacyPisVal = Math.max(0, item.legacyPisVal || 0);
  const legacyCofinsVal = Math.max(0, item.legacyCofinsVal || 0);
  const totalLegacyTaxesInDoc = Math.round((legacyIcmsVal + legacyPisVal + legacyCofinsVal) * 100) / 100;

  if (
    cleanCfop.startsWith('1556') || cleanCfop.startsWith('2556') ||
    cleanCfop.startsWith('1407') || cleanCfop.startsWith('2407') ||
    cleanCfop.startsWith('1551') || cleanCfop.startsWith('2551') ||
    destination === 'consumption'
  ) {
    // Uso e Consumo: Crédito vedado no regime atual (Art. 20 LC 87/96 e Leis 10.637/10.833)
    isCreditDisallowedToday = true;
    legacyCreditVal = 0;
  } else if (
    cleanCfop.startsWith('1933') || cleanCfop.startsWith('2933') ||
    destination === 'service'
  ) {
    // Serviços municipais / sem crédito no regime atual
    isCreditDisallowedToday = true;
    legacyCreditVal = 0;
  } else {
    // Compras para revenda ou industrialização
    if (totalLegacyTaxesInDoc > 0) {
      legacyCreditVal = totalLegacyTaxesInDoc;
    } else if (costPrice > 0 && supplierTaxRegime === 'regime_geral') {
      // Estimativa conservadora de crédito legado se não informado expressamente (~9,25% PIS/COFINS)
      legacyCreditVal = Math.round(costPrice * 0.0925 * 100) / 100;
    } else {
      legacyCreditVal = 0;
    }
  }

  const reformCreditVal = inputCreditVal;
  const reformCreditGainVal = Math.max(0, Math.round((reformCreditVal - legacyCreditVal) * 100) / 100);

  // 2. NOTA DE SAÍDA (VENDA / CLIENTE)
  const cbsRatePct = classification.cbsRate;
  const ibsRatePct = classification.ibsRate;
  const totalTaxRatePct = classification.totalRate;

  // Itens de Uso e Consumo NÃO são comercializados diretamente (não geram venda nem débito na ponta)
  const isConsumption = destination === 'consumption';

  // Identificação de Planilha Só de Vendas (Notas de Saída sem NF de Custo informada)
  const isSalesOnly = !isConsumption && (costPrice <= 0 || !Number.isFinite(costPrice)) && currentSalePrice > 0;

  // Preço de recomposição protetiva da venda (mantém a receita líquida atual após a introdução da CBS/IBS)
  const recomposedSalePrice = isSalesOnly
    ? arredondarPrecoFinal(currentSalePrice * (1 + totalTaxRatePct / 100))
    : 0;

  // Mark-up sobre a base líquida de custo ou recomposição sobre faturamento atual
  let baseLiquidaDesejada = 0;
  let precoBrutoCalculado = 0;
  let suggestedSalePrice = 0;

  if (isConsumption) {
    // Uso e consumo não é vendido: sem preço sugerido comercial
    suggestedSalePrice = 0;
  } else if (costPrice > 0) {
    const effectiveTargetMargin = item.targetMarginPct !== undefined ? item.targetMarginPct : defaultTargetMarginPct;
    const targetMarginDecimal = Math.min(0.85, Math.max(0.05, effectiveTargetMargin / 100));
    baseLiquidaDesejada = netStockCost > 0 ? netStockCost / (1 - targetMarginDecimal) : 0;
    precoBrutoCalculado = baseLiquidaDesejada * (1 + totalTaxRatePct / 100);
    suggestedSalePrice = arredondarPrecoFinal(precoBrutoCalculado);
  } else if (isSalesOnly) {
    // Quando não há custo de compra na planilha, sugere o preço recomposto protetivo
    suggestedSalePrice = recomposedSalePrice > 0 ? recomposedSalePrice : arredondarPrecoFinal(currentSalePrice);
  }

  // Preço ativo simulado:
  const simulatedSalePrice = isConsumption
    ? 0
    : (item.simulatedSalePrice && item.simulatedSalePrice > 0)
      ? item.simulatedSalePrice
      : isSalesOnly
        ? currentSalePrice
        : (suggestedSalePrice > 0 ? suggestedSalePrice : currentSalePrice);

  // CBS + IBS destacado na nota de saída
  const taxOnSaleVal = (!isConsumption && totalTaxRatePct > 0 && simulatedSalePrice > 0)
    ? Math.round((simulatedSalePrice * (totalTaxRatePct / (100 + totalTaxRatePct))) * 100) / 100
    : 0;

  // Decomposição dos impostos de Saída (CBS e IBS)
  let taxOnSaleCbsVal = 0;
  let taxOnSaleIbsVal = 0;
  if (taxOnSaleVal > 0 && totalTaxRatePct > 0) {
    const cbsRatioSale = cbsRatePct / totalTaxRatePct;
    taxOnSaleCbsVal = Math.round(taxOnSaleVal * cbsRatioSale * 100) / 100;
    taxOnSaleIbsVal = Math.round((taxOnSaleVal - taxOnSaleCbsVal) * 100) / 100;
  }

  // 3. CONFRONTO TRIBUTÁRIO & SPLIT PAYMENT
  // Imposto líquido a recolher = (Imposto da Saída) - (Crédito da Entrada)
  const netTaxPayableVal = isConsumption
    ? 0
    : Math.max(0, Math.round((taxOnSaleVal - inputCreditVal) * 100) / 100);
  const cbsToPayVal = isConsumption
    ? 0
    : Math.max(0, Math.round((taxOnSaleCbsVal - inputCreditCbsVal) * 100) / 100);
  const ibsToPayVal = isConsumption
    ? 0
    : Math.max(0, Math.round((taxOnSaleIbsVal - inputCreditIbsVal) * 100) / 100);

  // Sobra Bruta Unitária / Margem no Bolso
  let projectedMarginVal = 0;
  let projectedMarginPct = 0;

  if (isConsumption) {
    // O benefício do consumo é o crédito de imposto recuperado na apuração
    projectedMarginVal = inputCreditVal;
    projectedMarginPct = 0;
  } else if (costPrice > 0) {
    projectedMarginVal = Math.round((simulatedSalePrice - taxOnSaleVal - netStockCost) * 100) / 100;
    projectedMarginPct = simulatedSalePrice > 0 ? (projectedMarginVal / simulatedSalePrice) * 100 : 0;
  } else if (isSalesOnly) {
    // Sobra líquida de impostos na venda pura sem custo direto de compra informado
    projectedMarginVal = Math.round((simulatedSalePrice - taxOnSaleVal) * 100) / 100;
    projectedMarginPct = simulatedSalePrice > 0 ? (projectedMarginVal / simulatedSalePrice) * 100 : 0;
  }

  // Diagnóstico de Rentabilidade
  let status: 'HEALTHY' | 'COMPRESSED' | 'LOSS' = 'HEALTHY';
  let statusLabel = 'Margem Saudável';

  if (isConsumption) {
    status = 'HEALTHY';
    statusLabel = 'Crédito Operacional Pleno';
  } else if (isSalesOnly) {
    if (projectedMarginPct >= 20) {
      status = 'HEALTHY';
      statusLabel = 'Venda Líquida Saudável';
    } else if (projectedMarginPct >= 5) {
      status = 'COMPRESSED';
      statusLabel = 'Margem Comprimida';
    } else {
      status = 'LOSS';
      statusLabel = 'Margem Asfixiada';
    }
  } else {
    const priceToDiagnose = (item.simulatedSalePrice && item.simulatedSalePrice > 0)
      ? item.simulatedSalePrice
      : currentSalePrice;
    
    const marginValDiagnosed = priceToDiagnose > 0 ? priceToDiagnose - costPrice : -costPrice;
    const marginPctDiagnosed = priceToDiagnose > 0 ? (marginValDiagnosed / priceToDiagnose) * 100 : 0;

    if (marginValDiagnosed < 0 || marginPctDiagnosed < 5) {
      status = 'LOSS';
      statusLabel = 'Prejuízo Imediato';
    } else if (marginPctDiagnosed < 20) {
      status = 'COMPRESSED';
      statusLabel = 'Margem Comprimida';
    }
  }

  return {
    originalIndex: 0,
    sku: item.sku || '-',
    name: item.name || 'Item sem nome',
    fiscalType: classification.fiscalType,
    fiscalCode: classification.fiscalCode,
    reductionCategory: classification.category,
    reductionPct: classification.reductionPct,
    reductionLabel: classification.label,
    ncm: resolvedNcm || classification.fiscalCode,
    nbs: resolvedNbs || (classification.fiscalType === 'NBS' ? classification.fiscalCode : ''),
    destination,
    sector: detectedSector,
    cfop: item.cfop,
    paymentTermDays: item.paymentTermDays,
    isSalesOnly,
    recomposedSalePrice,
    originalCost: costPrice,
    supplierTaxRegime,
    inputCreditPct,
    inputCreditCbsRatePct,
    inputCreditIbsRatePct,
    inputCreditVal,
    inputCreditCbsVal,
    inputCreditIbsVal,
    netStockCost,
    cbsRatePct,
    ibsRatePct,
    totalTaxRatePct,
    currentSalePrice: isConsumption ? 0 : currentSalePrice,
    suggestedSalePrice,
    simulatedSalePrice,
    taxOnSaleVal,
    taxOnSaleCbsVal,
    taxOnSaleIbsVal,
    netTaxPayableVal,
    cbsToPayVal,
    ibsToPayVal,
    projectedMarginVal,
    projectedMarginPct,
    priceDifference: simulatedSalePrice - currentSalePrice,
    unitCost: item.unitCost,
    cfopDescription: item.cfopDescription,
    legacyCreditVal,
    legacyIcmsVal,
    legacyPisVal,
    legacyCofinsVal,
    reformCreditVal,
    reformCreditGainVal,
    isCreditDisallowedToday,
    status,
    statusLabel,
    classificationTag: classification.label,
  };
}

/**
 * Simula uma lista inteira de produtos em lote e gera o resumo executivo,
 * consolidando apuração de CBS/IBS, decomposição e métricas de Capital de Giro no Split Payment.
 */
export function simulateBatch(
  items: BatchItemInput[],
  regime: TaxRegimeMode = 'simples_hibrido',
  defaultTargetMarginPct = 25,
  benchmarkSource: BatchBenchmarkTaxSource = 'tcu_2026'
): {
  items: BatchItemSimulationResult[];
  summary: BatchSimulationSummary;
} {
  const simulated = items.map((item, idx) => ({
    ...simulateItem(item, regime, defaultTargetMarginPct, benchmarkSource),
    originalIndex: idx
  }));

  let totalPurchasesVal = 0;
  let totalStockCostBefore = 0;
  let totalStockCostAfter = 0;
  let totalInputCreditsGained = 0;
  let totalCbsCreditGained = 0;
  let totalIbsCreditGained = 0;
  let totalConsumptionItems = 0;
  let totalConsumptionCreditsGained = 0;
  let totalTaxOnSale = 0;
  let totalCbsDebitOnSale = 0;
  let totalIbsDebitOnSale = 0;
  let totalNetTaxPayable = 0;
  let totalLegacyCreditsVal = 0;
  let totalReformCreditsVal = 0;
  let totalNetCreditGainVal = 0;
  let disallowedCreditsCount = 0;
  let sumCurrentMarginPct = 0;
  let sumProjectedMarginPct = 0;
  let healthyCount = 0;
  let compressedCount = 0;
  let lossCount = 0;

  // Acumuladores de Prazos Médios (PMP e PMR)
  let sumPmp = 0;
  let countPmp = 0;
  let sumPmr = 0;
  let countPmr = 0;

  for (const res of simulated) {
    totalPurchasesVal += res.originalCost;
    totalStockCostBefore += res.originalCost;
    totalStockCostAfter += res.netStockCost;
    totalInputCreditsGained += res.inputCreditVal;
    totalCbsCreditGained += res.inputCreditCbsVal;
    totalIbsCreditGained += res.inputCreditIbsVal;

    totalLegacyCreditsVal += res.legacyCreditVal;
    totalReformCreditsVal += res.reformCreditVal;
    totalNetCreditGainVal += res.reformCreditGainVal;
    if (res.isCreditDisallowedToday) disallowedCreditsCount++;

    if (res.destination === 'consumption') {
      totalConsumptionItems++;
      totalConsumptionCreditsGained += res.inputCreditVal;
    }

    totalTaxOnSale += res.taxOnSaleVal;
    totalCbsDebitOnSale += res.taxOnSaleCbsVal;
    totalIbsDebitOnSale += res.taxOnSaleIbsVal;
    totalNetTaxPayable += res.netTaxPayableVal;

    const currentMarginVal = res.currentSalePrice - res.originalCost;
    const currentMarginPct = res.currentSalePrice > 0 ? (currentMarginVal / res.currentSalePrice) * 100 : 0;
    sumCurrentMarginPct += currentMarginPct;
    sumProjectedMarginPct += res.projectedMarginPct;

    if (res.status === 'HEALTHY') healthyCount++;
    else if (res.status === 'COMPRESSED') compressedCount++;
    else lossCount++;

    if (res.paymentTermDays && res.paymentTermDays > 0) {
      sumPmr += res.paymentTermDays;
      countPmr++;
    }
  }

  // Verifica prazos informados nos itens de entrada
  items.forEach(it => {
    if (it.purchaseTermDays && it.purchaseTermDays > 0) {
      sumPmp += it.purchaseTermDays;
      countPmp++;
    }
  });

  const count = simulated.length || 1;

  // Prazos Médios Padrão de Mercado para benchmark caso a planilha não tenha colunas de prazo
  const avgPmpDays = countPmp > 0 ? Math.round(sumPmp / countPmp) : 28; // Prazo médio de pagamento a fornecedor (dias)
  const avgPmrDays = countPmr > 0 ? Math.round(sumPmr / countPmr) : 35; // Prazo médio de recebimento de clientes (dias)
  const avgPmeDays = 20; // Prazo médio de estocagem (dias)
  const operatingCycleDays = avgPmeDays + avgPmrDays;
  const financialCycleDays = Math.max(0, operatingCycleDays - avgPmpDays);
  const idealReceivingDaysForSplit = Math.max(7, Math.round(avgPmpDays - 5));

  const workingCapitalMetrics: WorkingCapitalMetrics = {
    avgPmpDays,
    avgPmrDays,
    avgPmeDays,
    operatingCycleDays,
    financialCycleDays,
    splitPaymentDrainVal: Math.round(totalNetTaxPayable * 100) / 100,
    idealReceivingDaysForSplit
  };

  const summary: BatchSimulationSummary = {
    totalItems: simulated.length,
    healthyCount,
    compressedCount,
    lossCount,
    totalPurchasesVal: Math.round(totalPurchasesVal * 100) / 100,
    totalStockCostBefore: Math.round(totalStockCostBefore * 100) / 100,
    totalStockCostAfter: Math.round(totalStockCostAfter * 100) / 100,
    totalInputCreditsGained: Math.round(totalInputCreditsGained * 100) / 100,
    totalCbsCreditGained: Math.round(totalCbsCreditGained * 100) / 100,
    totalIbsCreditGained: Math.round(totalIbsCreditGained * 100) / 100,
    totalConsumptionItems,
    totalConsumptionCreditsGained: Math.round(totalConsumptionCreditsGained * 100) / 100,
    totalTaxOnSale: Math.round(totalTaxOnSale * 100) / 100,
    totalCbsDebitOnSale: Math.round(totalCbsDebitOnSale * 100) / 100,
    totalIbsDebitOnSale: Math.round(totalIbsDebitOnSale * 100) / 100,
    totalNetTaxPayable: Math.round(totalNetTaxPayable * 100) / 100,
    totalLegacyCreditsVal: Math.round(totalLegacyCreditsVal * 100) / 100,
    totalReformCreditsVal: Math.round(totalReformCreditsVal * 100) / 100,
    totalNetCreditGainVal: Math.round(totalNetCreditGainVal * 100) / 100,
    disallowedCreditsCount,
    averageCurrentMarginPct: Math.round((sumCurrentMarginPct / count) * 10) / 10,
    averageProjectedMarginPct: Math.round((sumProjectedMarginPct / count) * 10) / 10,
    workingCapitalMetrics
  };

  return { items: simulated, summary };
}
